import { Copy } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";

import type { DnsRecord } from "../../api/types";
import { recordStatus } from "../../model/domainStatus";
import { copyText } from "./copyText";
import { ToneBadge } from "./ToneBadge";

/**
 * 屏 5 第 3 步里的 DNS 表:记录类型 / 主机记录 / 记录值 / 状态,逐行照 api `records` 画。
 *
 * ★ 行数、值、备注全是 api 给的(TXT `_siteverify.www` / CNAME `www`;裸域时 CNAME 主机是 `@`),这里不补行、不拼值。
 *   api 这期**没有 A 行**(裸域 301 要网关 IP,发布节点未定,规格 §9.1),前端也不画。
 * ★ 主机记录相对主域算(附录 A.37):api 给了 `zone` + `fqdn` 时,主机下面一行小字「在 {zone} 的解析里添加;
 *   完整名 {fqdn}」—— 用户在 DNS 控制台里找的是主域那一页,填的是相对名。
 * ★ 记录值要让用户抄进 DNS 控制台,每行带复制按钮;状态下面一行小字是 api 的 `note`(上次检测时间 + 人话)。
 */
export function DnsTable({ records }: { records: DnsRecord[] }) {
  return (
    <div className="rounded-md border border-border">
      <Table aria-label="DNS 记录">
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">记录类型</TableHead>
            <TableHead className="w-64">主机记录</TableHead>
            <TableHead>记录值</TableHead>
            <TableHead className="w-52">状态</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r) => {
            const st = recordStatus(r.status);
            return (
              <TableRow key={`${r.type}:${r.host}`}>
                <TableCell className="font-mono text-xs font-semibold">{r.type}</TableCell>
                <TableCell>
                  <span className="font-mono text-xs">{r.host}</span>
                  {r.zone && r.fqdn && (
                    // 两行:「在哪一页加」一行,「完整名」一行;长域名只在放不下时才断(不再逐字母硬断)。
                    <p className="mt-1 text-2xs leading-snug text-muted-foreground">
                      <span className="block">在 {r.zone} 的解析里添加;</span>
                      <span className="block [overflow-wrap:anywhere]">
                        完整名 <span className="font-mono">{r.fqdn}</span>
                      </span>
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <code className="break-all font-mono text-xs text-foreground">{r.value}</code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 shrink-0 p-0"
                      aria-label="复制记录值"
                      title="复制记录值"
                      onClick={() => void copyText(r.value, "记录值")}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <ToneBadge tone={st.tone}>{st.label}</ToneBadge>
                  {r.note && <p className="mt-1 text-2xs text-muted-foreground">{r.note}</p>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
